import React from 'react';
import { CHALLENGE_URL } from '../constants.ts';

const Hero: React.FC = () => {
  return (
    <section className="relative bg-gradient-to-br from-primary-50 via-primary-100 to-green-200 bg-texture min-h-screen flex items-center overflow-hidden">
      {/* Enhanced background with animated elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 to-primary-100/50"></div>
      
      {/* Floating decorative elements */}
      <div className="absolute top-20 left-10 animate-float opacity-20">
        <div className="w-20 h-20 bg-primary-300 rounded-full blur-xl"></div>
      </div>
      <div className="absolute top-40 right-20 animate-float-staggered opacity-30">
        <div className="w-16 h-16 bg-green-300 rounded-full blur-xl"></div>
      </div>
      <div className="absolute bottom-40 left-1/4 animate-float opacity-25">
        <div className="w-12 h-12 bg-primary-400 rounded-full blur-lg"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 section-padding">
        <div className="text-center">
          {/* Enhanced Badge */}
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-primary-100 to-green-100 text-primary-700 text-sm font-medium mb-8 glow card-hover">
            <span className="mr-2 text-lg">🎴</span>
            Nouveau jeu de cartes stratégique
          </div>

          {/* Enhanced Main Title */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-gray-900 mb-6 fade-in-up">
            Jeu <span className="gradient-text">151</span>
          </h1>

          {/* Enhanced Slogan */}
          <p className="text-2xl md:text-3xl text-gray-700 mb-8 font-medium slide-in-left">
            Dépassez les règles, maîtrisez la stratégie
          </p>

          {/* Enhanced Description */}
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto mb-12 leading-relaxed fade-in-up">
            Jeu 151 est un jeu de cartes stratégique basé sur les 32 cartes traditionnelles. 
            Jouez vos 8 pour changer la couleur, maîtrisez les AS pour bloquer vos adversaires, 
            et soyez le dernier à dépasser les 151 points !
          </p>

          {/* Enhanced CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <a
              href="https://play.google.com/store/apps/details?id=com.djammadev.game.i151"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary btn-animate flex items-center px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 animate-bounce"
            >
              <svg className="w-7 h-7 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              Télécharger sur Android
            </a>
            
            <a
              href="https://apps.apple.com/fr/app/i151/id6748642726?l=en-GB"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary btn-animate flex items-center px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <svg className="w-7 h-7 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
              </svg>
              Télécharger sur iOS
            </a>
          </div>

          <p className="text-center mb-16">
            <a
              href={CHALLENGE_URL}
              className="inline-flex items-center gap-2 text-primary-700 hover:text-primary-800 font-semibold text-lg underline-offset-4 hover:underline transition-colors"
            >
              <span aria-hidden="true">🤖</span>
              Challenge IA — créez un bot, affrontez les autres équipes
            </a>
          </p>

          {/* Enhanced Stats with animations */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center card-hover p-6 rounded-2xl bg-white/50 backdrop-blur-sm">
              <div className="text-4xl font-bold gradient-text mb-2">32</div>
              <div className="text-gray-600 font-medium">Cartes traditionnelles</div>
            </div>
            <div className="text-center card-hover p-6 rounded-2xl bg-white/50 backdrop-blur-sm">
              <div className="text-4xl font-bold gradient-text mb-2">151</div>
              <div className="text-gray-600 font-medium">Points à dépasser</div>
            </div>
            <div className="text-center card-hover p-6 rounded-2xl bg-white/50 backdrop-blur-sm">
              <div className="text-4xl font-bold gradient-text mb-2">∞</div>
              <div className="text-gray-600 font-medium">Stratégies possibles</div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-primary-400 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 